/**
 * Diálogo de confirmación con estilo Atreus — reemplaza `window.confirm()`
 * nativo en toda la app para mantener coherencia visual.
 *
 *   const { confirm, dialog } = useConfirm();
 *
 *   const ok = await confirm({
 *     title: '¿Vaciar carrito?',
 *     message: 'Se quitarán todos los items.',
 *     confirmLabel: 'Vaciar',
 *     variant: 'danger',
 *   });
 *   if (ok) clearItems.mutate(...);
 *
 *   // En el render:
 *   <>{dialog}<OtraCosa /></>
 */

import { Button } from '@atreus/ui';
import { AlertTriangle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
}

interface InternalState {
  open: boolean;
  options: ConfirmOptions;
  resolve: ((v: boolean) => void) | null;
}

const DEFAULT_OPTIONS: ConfirmOptions = { title: '' };

export const useConfirm = (): {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  dialog: ReactElement;
} => {
  const [state, setState] = useState<InternalState>({
    open: false,
    options: DEFAULT_OPTIONS,
    resolve: null,
  });

  const confirm = useCallback(
    (options: ConfirmOptions): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        setState({ open: true, options, resolve });
      }),
    [],
  );

  const close = useCallback(
    (value: boolean) => {
      setState((s) => {
        s.resolve?.(value);
        return { open: false, options: s.options, resolve: null };
      });
    },
    [],
  );

  return {
    confirm,
    dialog: (
      <ConfirmDialog
        open={state.open}
        options={state.options}
        onCancel={() => close(false)}
        onConfirm={() => close(true)}
      />
    ),
  };
};

const ConfirmDialog = ({
  open,
  options,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  options: ConfirmOptions;
  onCancel: () => void;
  onConfirm: () => void;
}): ReactElement => {
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Autofocus en "Confirmar" al abrir + atajos Escape/Enter.
  useEffect(() => {
    if (!open) return;
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        onConfirm();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel, onConfirm]);

  if (!open) return <></>;

  const {
    title,
    message,
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
    variant = 'default',
  } = options;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="atreus-confirm-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface-raised shadow-xl">
        <div className="flex items-start gap-3 p-5">
          {variant === 'danger' ? (
            <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-danger/10 text-danger">
              <AlertTriangle className="size-5" />
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <h2
              id="atreus-confirm-title"
              className="text-lg font-black tracking-tight"
            >
              {title}
            </h2>
            {message ? (
              <p className="mt-1 text-sm text-fg-muted">{message}</p>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="ghost" size="md" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant={variant === 'danger' ? 'danger' : 'default'}
            size="md"
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};
